import React, { useEffect, useMemo, useRef, useState } from 'react';

import WorklistNewOrderDetail from '@/components/reconciliation/WorklistNewOrderDetail';
import WorklistOrderChangeDetail from '@/components/reconciliation/WorklistOrderChangeDetail';
import WorklistRunDetail from '@/components/reconciliation/WorklistRunDetail';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { buildWorklistRecords, groupWorklistRecords, WORKLIST_GROUPS } from '@/utils/worklistReviewModel';

const REVIEW_STAGES = [
  { id: 'new-orders', label: 'New Orders' },
  { id: 'order-changes', label: 'Order Changes' },
  { id: 'runs', label: 'Runs' },
];

const factLine = (record) => {
  const facts = [record.detail];
  if (record.needsReview) facts.push(`${record.needsReview} to review`);
  if (record.sendable) facts.push(`${record.sendable} ready`);
  if (record.blocked) facts.push(`${record.blocked} waiting`);
  return facts.filter(Boolean).join(' · ');
};

export default function WorklistReviewPreview({
  activeStage, onStageChange, stageCounts, newOrders, orderConflicts,
  deletionRequests, offlineRuns, cachedRuns, validations,
  duplicateAcknowledgments, newOrderActions, offlineRunActions,
  cachedRunActions, orderConflictActions, deletionActions, isAdmin,
}) {
  const records = useMemo(() => buildWorklistRecords({
    stage: activeStage, newOrders, orderConflicts, deletionRequests,
    offlineRuns, cachedRuns, validations, duplicateAcknowledgments, isAdmin,
  }), [
    activeStage, cachedRuns, deletionRequests, duplicateAcknowledgments,
    isAdmin, newOrders, offlineRuns, orderConflicts, validations,
  ]);
  const grouped = useMemo(() => groupWorklistRecords(records), [records]);
  const orderedRecords = useMemo(
    () => WORKLIST_GROUPS.flatMap(group => grouped[group.id]),
    [grouped],
  );
  const [selectedId, setSelectedId] = useState(null);
  const previousSelectedId = useRef(null);
  const railButtons = useRef(new Map());

  useEffect(() => {
    if (!records.some(record => record.id === selectedId)) {
      setSelectedId(records[0]?.id || null);
    }
  }, [records, selectedId]);

  useEffect(() => {
    if (
      previousSelectedId.current
      && previousSelectedId.current !== selectedId
      && !records.some(record => record.id === previousSelectedId.current)
    ) {
      railButtons.current.get(selectedId)?.focus();
    }
    previousSelectedId.current = selectedId;
  }, [records, selectedId]);

  const moveRailSelection = (event, recordId) => {
    const currentIndex = orderedRecords.findIndex(record => record.id === recordId);
    let nextIndex = null;
    if (event.key === 'ArrowDown') nextIndex = Math.min(currentIndex + 1, orderedRecords.length - 1);
    if (event.key === 'ArrowUp') nextIndex = Math.max(currentIndex - 1, 0);
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = orderedRecords.length - 1;
    if (nextIndex === null || nextIndex === currentIndex) return;
    event.preventDefault();
    const nextId = orderedRecords[nextIndex]?.id;
    if (!nextId) return;
    setSelectedId(nextId);
    railButtons.current.get(nextId)?.focus();
  };

  const selected = records.find(record => record.id === selectedId) || records[0];

  return (
    <div data-testid="worklist-review-preview" className="overflow-hidden rounded-xl border border-stone-300 bg-white">
      <div className="border-b border-stone-300 px-5 py-4">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold text-stone-900">Review work done on this computer</h2>
          <span className="text-sm text-stone-500">{records.length} item{records.length === 1 ? '' : 's'} in this category</span>
        </div>
      </div>
      <Tabs value={activeStage} onValueChange={onStageChange}>
        <TabsList className="grid h-auto w-full grid-cols-3 rounded-none border-b border-stone-300 bg-white p-0">
          {REVIEW_STAGES.map(stage => (
            <TabsTrigger key={stage.id} value={stage.id} className="rounded-none py-3">
              {stage.label} ({stageCounts[stage.id] || 0})
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="grid min-h-[430px] md:grid-cols-[270px_minmax(0,1fr)]">
        <aside className="border-b border-stone-300 bg-stone-50 md:max-h-[70vh] md:overflow-y-auto md:border-b-0 md:border-r" aria-label="Review worklist">
          {WORKLIST_GROUPS.map(group => (
            <div key={group.id} className="py-3">
              <div className="px-4 pb-2 text-[11px] font-bold uppercase tracking-widest text-stone-600">
                {group.label} · {grouped[group.id].length}
              </div>
              {grouped[group.id].map(record => (
                <button
                  key={record.id}
                  type="button"
                  onClick={() => setSelectedId(record.id)}
                  onKeyDown={event => moveRailSelection(event, record.id)}
                  ref={element => {
                    if (element) railButtons.current.set(record.id, element);
                    else railButtons.current.delete(record.id);
                  }}
                  className={`w-full border-l-4 px-4 py-3 text-left ${selected?.id === record.id ? 'border-stone-900 bg-white' : 'border-transparent hover:bg-white/70'} ${group.id === 'waiting' ? 'opacity-60' : ''}`}
                  aria-current={selected?.id === record.id ? 'true' : undefined}
                  aria-label={`${record.title}. ${factLine(record)}`}
                >
                  <div className="font-medium text-stone-900">{record.title}</div>
                  <div className="mt-1 text-xs text-stone-500">{factLine(record)}</div>
                </button>
              ))}
            </div>
          ))}
        </aside>
        <section className="min-w-0 p-4 sm:p-5 md:p-7" aria-live="polite">
          {selected ? (
            activeStage === 'runs' ? (
              <WorklistRunDetail
                key={selected.id}
                record={selected}
                offlineRunActions={offlineRunActions}
                cachedRunActions={cachedRunActions}
                isAdmin={isAdmin}
                onSelectRecord={setSelectedId}
              />
            ) : activeStage === 'order-changes' ? (
              <WorklistOrderChangeDetail
                key={selected.id}
                record={selected}
                conflictActions={orderConflictActions}
                deletionActions={deletionActions}
                isAdmin={isAdmin}
              />
            ) : (
              <WorklistNewOrderDetail key={selected.id} record={selected} actions={newOrderActions} />
            )
          ) : (
            <div className="py-12 text-center text-stone-500">No work is waiting in this category.</div>
          )}
        </section>
      </div>
    </div>
  );
}
